import type { TMDbMovie } from '../services/tmdbApi';
import { areMovieIdsEquivalent } from './movieUtils';

const STORAGE_KEY = 'watchlists_movie_nav_preview_v1';
const MAX_AGE_MS = 120_000;

/**
 * React 18 Strict Mode runs layout twice; we remove from sessionStorage on first read.
 * Hold one replay so the second layout pass still gets the same preview, then clear.
 */
let strictModeReplayPreview: MovieDetailNavPreviewStash | null = null;

export interface MovieDetailNavPreviewStash {
  movieId: string;
  title: string;
  year: string;
  posterUrl: string | null;
  posterPath: string | null;
  tmdbNumericId: number | null;
  stashedAt: number;
}

function idsMatchForNav(stashedId: string, routeMovieId: string): boolean {
  if (stashedId === routeMovieId) return true;
  try {
    if (decodeURIComponent(stashedId) === decodeURIComponent(routeMovieId)) return true;
  } catch {
    /* ignore */
  }
  return areMovieIdsEquivalent(stashedId, routeMovieId);
}

/**
 * Call immediately before navigating to `/movie/:id` so Movie detail can paint title/year/poster without waiting for API.
 */
export function stashMovieDetailNavPreviewFromMovie(
  navigationMovieId: string,
  movie: {
    Title?: string;
    title?: string;
    Year?: string;
    year?: string;
    release_date?: string;
    Poster?: string;
    poster_path?: string | null;
    id?: number | string;
  },
  posterUrlResolver: () => string | null
): void {
  if (!navigationMovieId || navigationMovieId === 'unknown') return;
  const title = movie.Title || movie.title || '';
  const year =
    movie.Year ||
    movie.year ||
    (movie.release_date ? movie.release_date.split('-')[0] : '') ||
    '';
  let tmdbNumericId: number | null = null;
  if (typeof movie.id === 'number' && Number.isFinite(movie.id)) {
    tmdbNumericId = movie.id;
  } else if (typeof movie.id === 'string' && /^\d+$/.test(movie.id.trim())) {
    tmdbNumericId = Number(movie.id.trim());
  }
  const posterPath =
    movie.poster_path && movie.poster_path !== 'N/A' ? String(movie.poster_path) : null;
  const payload: MovieDetailNavPreviewStash = {
    movieId: navigationMovieId,
    title,
    year,
    posterUrl: posterUrlResolver(),
    posterPath,
    tmdbNumericId,
    stashedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

/**
 * Consume one stashed preview for this route `movieId`. Returns null if missing, stale, or id mismatch.
 */
export function takeMovieDetailNavPreview(expectedRouteMovieId: string): MovieDetailNavPreviewStash | null {
  if (!expectedRouteMovieId) return null;

  if (strictModeReplayPreview) {
    if (!idsMatchForNav(strictModeReplayPreview.movieId, expectedRouteMovieId)) {
      strictModeReplayPreview = null;
    } else {
      const replay = strictModeReplayPreview;
      strictModeReplayPreview = null;
      if (Date.now() - (replay.stashedAt || 0) > MAX_AGE_MS) return null;
      return replay;
    }
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const data = JSON.parse(raw) as MovieDetailNavPreviewStash;
    if (!data?.movieId) return null;
    if (Date.now() - (data.stashedAt || 0) > MAX_AGE_MS) return null;
    if (!idsMatchForNav(data.movieId, expectedRouteMovieId)) return null;
    strictModeReplayPreview = data;
    return data;
  } catch {
    return null;
  }
}

/** Call once after layout (e.g. `useEffect`) so a single layout pass does not leave replay stuck for a later navigation to the same id. */
export function clearMovieDetailNavPreviewStrictReplay(): void {
  strictModeReplayPreview = null;
}

/** Minimal `TMDbMovie` for instant paint; full fetch replaces this. */
export function tmdbMovieFromNavPreview(routeMovieId: string, p: MovieDetailNavPreviewStash): TMDbMovie {
  const Poster =
    p.posterUrl && p.posterUrl.length > 0
      ? p.posterUrl
      : 'N/A';
  const imdbID = routeMovieId.startsWith('tt')
    ? routeMovieId
    : p.tmdbNumericId != null
      ? `tmdb_${p.tmdbNumericId}`
      : routeMovieId;
  const title = p.title || '…';
  return {
    Response: 'True',
    Title: title,
    title,
    Year: p.year || '',
    Poster,
    poster_path: p.posterPath || undefined,
    imdbID,
    id: p.tmdbNumericId ?? undefined,
  } as TMDbMovie;
}
